import React, { useContext } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/DraftControlPanel';
import block from 'propmods';

import { Context as SnapshotContext } from '../../../context/snapshotContext';
import { useCurrentAliceApp } from '../../../hooks/useCurrentAliceApp';
import { FetchingHandlerState } from '../../../hooks/useFetchingHandler';
import { completedModerationStateTexts } from '../../../locale/ru/draftStatus';
import {
    DevicesTestingStatusMeta,
    initialDevicesTestingOptions,
    parseDevicesTestingOptions,
    TestingDateStatus,
} from '../../../model/devicesTestingForm';
import { formatDevicesTestingRange } from '../../../utils/time';
import Caption from '../../Form/Caption';
import { FormError } from '../../Form/FormError';
import { Loading } from '../../Loading/Loading';
import { TestingDateSelectionChanger } from '../TestingDateSelectionChanger/TestingDateSelectionChanger';
import { TestingDateSelectionForm } from '../TestingDateSelectionForm/TestingDateSelectionForm';
import { getOnlineDevicesTestingNoteText } from '../text';

import './TestingDateSelectionSection.scss';

const b = block('TestingDateSelectionSection');
const t = i18n(keyset);

interface TestingDateSelectionSectionProps {
    state: FetchingHandlerState;
    devicesTestingStatusMeta: DevicesTestingStatusMeta | undefined;
    loadStatusUnhandled: () => Promise<void>;
    disabled: boolean;
}

export const TestingDateSelectionSection: React.FC<TestingDateSelectionSectionProps> = ({
    state,
    devicesTestingStatusMeta,
    loadStatusUnhandled,
    disabled,
}) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const skill = useCurrentAliceApp()!;
    const { refreshSnapshot } = useContext(SnapshotContext);

    return (
        <div {...b()}>
            {(() => {
                switch (state) {
                    case 'loading':
                        return <Loading size="m" />;
                    case 'error':
                        return <FormError>{t('status-error')}</FormError>;
                    case 'idle': {
                        if (!devicesTestingStatusMeta) {
                            return null;
                        }

                        const data = devicesTestingStatusMeta;

                        switch (data.status) {
                            case TestingDateStatus.NOT_SPECIFIED:
                                return (
                                    <TestingDateSelectionForm
                                        initialSettings={initialDevicesTestingOptions}
                                        afterSave={loadStatusUnhandled}
                                        disabled={disabled}
                                    />
                                );
                            case TestingDateStatus.AWAITING:
                                return (
                                    <TestingDateSelectionChanger
                                        beforeButtonElement={
                                            <div {...b('text', { theme: 'default' })}>{t('meeting-wait-approval')}</div>
                                        }
                                        formSettings={parseDevicesTestingOptions(data.record)}
                                        afterButtonElement={<Caption>{getOnlineDevicesTestingNoteText()}</Caption>}
                                        afterFormSave={loadStatusUnhandled}
                                        disabled={disabled}
                                    />
                                );
                            case TestingDateStatus.REJECTED:
                                return (
                                    <TestingDateSelectionChanger
                                        beforeButtonElement={
                                            <div {...b('text', { theme: 'error' })}>{t('meeting-not-agreed')}</div>
                                        }
                                        formSettings={parseDevicesTestingOptions(data.record)}
                                        afterFormSave={loadStatusUnhandled}
                                        disabled={disabled}
                                    />
                                );
                            case TestingDateStatus.APPROVED: {
                                const formSettings = parseDevicesTestingOptions(data.record);

                                const approvedText = formSettings.selectedDateRange
                                    ? `${t('meeting-approved-on')}\xa0${formatDevicesTestingRange(
                                          formSettings.selectedDateRange,
                                          true,
                                      )}`
                                    : t('meeting-approved');

                                return (
                                    <TestingDateSelectionChanger
                                        beforeButtonElement={
                                            <div {...b('text', { theme: 'success' })}>{approvedText}</div>
                                        }
                                        formSettings={parseDevicesTestingOptions(data.record)}
                                        afterFormSave={refreshSnapshot}
                                        disabled={disabled}
                                    />
                                );
                            }
                            case TestingDateStatus.PASSED: {
                                return (
                                    <div {...b('text', { theme: 'success' })}>
                                        {
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            (completedModerationStateTexts as any)[skill.draft.channel][
                                                skill.draft.status
                                            ]
                                        }
                                    </div>
                                );
                            }
                            default:
                                return null;
                        }
                    }
                }
            })()}
        </div>
    );
};
